/** Dependency-free SVG charts for the analytics screen. */
import { useId, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { formatCurrency, formatDate, formatNumber } from '@/lib/format';

export interface BarDatum {
  label: string;
  value: number;
  caption?: string;
}

/** Horizontal bar list — used for reasons, refund modes, and the funnel. */
export function HorizontalBars({ data }: { data: BarDatum[] }) {
  const maxValue = Math.max(1, ...data.map((datum) => datum.value));
  if (data.length === 0) {
    return <p className="py-6 text-center text-sm text-ink-muted">No data for this period.</p>;
  }
  return (
    <ul className="flex flex-col gap-2.5">
      {data.map((datum) => (
        <li key={datum.label}>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="truncate pr-2 text-ink">{datum.label}</span>
            <span className="font-medium text-ink-muted">{datum.caption ?? datum.value}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
            <div
              className="h-full rounded-full bg-brand"
              style={{ width: `${Math.round((datum.value / maxValue) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
  /** Server-computed percentage for the tooltip. Falls back to value/total when omitted. */
  pct?: number;
  /** Extra currency amount shown in the tooltip, e.g. total refunded via this mode. */
  amount?: number;
}

/** Ring/donut chart with a centred label and a per-segment hover tooltip. */
export function DonutChart({
  segments,
  centerTop,
  centerBottom,
  size = 176,
  thickness = 24,
}: {
  segments: DonutSegment[];
  centerTop?: string;
  centerBottom?: string;
  size?: number;
  thickness?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<{ index: number; xPos: number; yPos: number } | null>(null);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  function pointerPosition(event: PointerEvent<SVGCircleElement>): { xPos: number; yPos: number } | null {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) {
      return null;
    }
    return { xPos: event.clientX - bounds.left, yPos: event.clientY - bounds.top };
  }

  function focusPosition(index: number): { xPos: number; yPos: number } {
    const angle = (offsetAtIndex(segments, index, circumference, total) / circumference) * 2 * Math.PI - Math.PI / 2;
    return {
      xPos: size / 2 + Math.cos(angle) * (radius + thickness / 2),
      yPos: size / 2 + Math.sin(angle) * (radius + thickness / 2),
    };
  }

  const activeSegment = hovered ? segments[hovered.index] : null;

  return (
    <div ref={containerRef} className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full -rotate-90" role="img" aria-label="Distribution">
        {segments.map((segment, index) => {
          const length = (segment.value / total) * circumference;
          const isActive = hovered?.index === index;
          const element = (
            <circle
              key={segment.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={isActive ? thickness + 4 : thickness}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
              tabIndex={0}
              role="img"
              aria-label={`${segment.label}: ${formatNumber(segment.value)}`}
              className="cursor-pointer outline-none transition-[stroke-width]"
              style={{ pointerEvents: 'stroke' }}
              onPointerMove={(event) => {
                const position = pointerPosition(event);
                if (position) {
                  setHovered({ index, ...position });
                }
              }}
              onPointerLeave={() => setHovered((current) => (current?.index === index ? null : current))}
              onFocus={() => setHovered({ index, ...focusPosition(index) })}
              onBlur={() => setHovered((current) => (current?.index === index ? null : current))}
            />
          );
          offset += length;
          return element;
        })}
      </svg>
      {centerTop || centerBottom ? (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          {centerTop ? <span className="text-lg font-bold text-ink">{centerTop}</span> : null}
          {centerBottom ? <span className="text-xs text-ink-subtle">{centerBottom}</span> : null}
        </div>
      ) : null}
      {activeSegment && hovered ? (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs shadow-pop"
          style={{ left: hovered.xPos, top: hovered.yPos - 10 }}
        >
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 flex-shrink-0 rounded-sm" style={{ backgroundColor: activeSegment.color }} />
            <span className="whitespace-nowrap font-semibold text-ink">{activeSegment.label}</span>
          </div>
          <div className="mt-0.5 whitespace-nowrap text-ink-subtle">
            {formatNumber(activeSegment.value)} ·{' '}
            {activeSegment.pct ?? Math.round((activeSegment.value / total) * 100)}%
          </div>
          {activeSegment.amount != null ? (
            <div className="whitespace-nowrap text-ink-subtle">{formatCurrency(activeSegment.amount)}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Cumulative stroke offset up to (not including) `index`, used to place focus tooltips. */
function offsetAtIndex(segments: DonutSegment[], index: number, circumference: number, total: number): number {
  let offset = 0;
  for (let cursor = 0; cursor < index; cursor += 1) {
    offset += (segments[cursor].value / total) * circumference;
  }
  return offset;
}

/** Simple responsive line+area trend chart with a crosshair + point tooltip. */
export function TrendChart({ points }: { points: { date: string; count: number }[] }) {
  const gradientId = useId();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  if (points.length === 0) {
    return <p className="py-6 text-center text-sm text-ink-muted">No data for this period.</p>;
  }

  const width = 600;
  const height = 160;
  const paddingY = 12;
  const maxCount = Math.max(1, ...points.map((point) => point.count));
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;

  const coordinates = points.map((point, index) => {
    const xCoord = index * stepX;
    const yCoord = height - paddingY - (point.count / maxCount) * (height - paddingY * 2);
    return { xCoord, yCoord };
  });

  const linePath = coordinates
    .map((coordinate, index) => `${index === 0 ? 'M' : 'L'} ${coordinate.xCoord.toFixed(1)} ${coordinate.yCoord.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  function indexFromPointer(event: PointerEvent<SVGRectElement>): number {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - bounds.left) / bounds.width;
    const rawIndex = stepX > 0 ? ratio * (points.length - 1) : 0;
    return Math.min(points.length - 1, Math.max(0, Math.round(rawIndex)));
  }

  function handleKeyDown(event: KeyboardEvent<SVGRectElement>) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(points.length - 1, (current ?? -1) + 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(0, (current ?? points.length) - 1));
    }
  }

  const active = activeIndex != null ? { point: points[activeIndex], coord: coordinates[activeIndex] } : null;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-40 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Returns over time"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-fill, #4f46e5)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--chart-fill, #4f46e5)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path d={linePath} fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {active ? (
          <line
            x1={active.coord.xCoord}
            x2={active.coord.xCoord}
            y1={0}
            y2={height}
            stroke="currentColor"
            className="text-ink-subtle/30"
            strokeWidth={1}
          />
        ) : null}
        {active ? (
          <circle cx={active.coord.xCoord} cy={active.coord.yCoord} r={4} fill="#4f46e5" stroke="white" strokeWidth={2} />
        ) : null}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="transparent"
          tabIndex={0}
          className="cursor-crosshair outline-none"
          onPointerMove={(event) => setActiveIndex(indexFromPointer(event))}
          onPointerLeave={() => setActiveIndex(null)}
          onFocus={() => setActiveIndex((current) => current ?? points.length - 1)}
          onBlur={() => setActiveIndex(null)}
          onKeyDown={handleKeyDown}
        />
      </svg>
      {active ? (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs shadow-pop"
          style={{
            left: `${(active.coord.xCoord / width) * 100}%`,
            top: `${Math.max(0, (active.coord.yCoord / height) * 100 - 14)}%`,
          }}
        >
          <div className="font-semibold text-ink">{formatNumber(active.point.count)} returns</div>
          <div className="text-ink-subtle">{formatDate(active.point.date)}</div>
        </div>
      ) : null}
    </div>
  );
}
